<template>
	<div class="content-page content-page--beta-features-settings">
		<div ref="contentPageHeading" class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title" v-show="!loading">
					Beta Feature Settings - {{ betaFeatureDescription }}
				</h2>
			</div>
			<div class="content-page__actions">
				<Button
					labelText="Go Live"
					v-if="showGoLiveButton"
					:btnClick="goLive"
					:title="'Select to go live.'"
				/>
			</div>

			<portal to="toastPortalDestination">
				<ToastV2
					ref="simpleToastRef"
					className="toast-v2--error"
					:useIcon="true"
					:timedDismissable="false"
					:userDismissable="true"
				>
					<span class="environment-summary__toast-text">
						{{
							`Unable to save changes due to Internal Error "${toastMessage}". Please try again or contact support to resolve the error`
						}}
					</span>
				</ToastV2>
			</portal>
		</div>
		<div class="beta-features-settings-content" v-show="!loading">
			<Heading
				:title="'Clients / Companies / Locations'"
				:variant="'medium'"
				:showIcone="false"
			/>

			<CCLSelector
				:items="items"
				@change="updateItems"
				@active-clients="updateClientsSelected"
				v-if="!loading && items.length"
			/>
		</div>
		<Spinner :processor="{ loading: loading }" />
		<transition name="slide-up">
			<div
				ref="mainContentFooter"
				class="beta-features-settings-content__footer "
				:class="{ 'is-fixed-footer': true }"
				v-show="!loading"
			>
				<div class="beta-features-settings-content__footer-container">
					<div class="beta-features-settings-content__footer-actions">
						<ButtonGroup
							:items="[
								{
									btnClick: save,
									className: 'btn--primary',
									labelText: 'Save & Close',
									disabled: !enabledSaveBttn
								},
								{
									btnClick: cancel,
									className: 'btn btn--link',
									labelText: 'Cancel'
								}
							]"
						/>
					</div>
				</div>
			</div>
		</transition>

		<Modals
			v-model="showGoLiveModal"
			type="confirmation"
			:title="titleGoLiveModal"
			labelConfirmButton="Yes, enable for all"
			labelCancelButton="Cancel"
			@confirm="confirmGoLive"
			@cancel="cancelGoLive"
		/>
		<Modals
			v-model="displayConfirmationModal"
			type="confirmation"
			ref="confirmationDialogue"
			:title="'Are you sure you want to leave this page?'"
			:confirmationContent="'Unsaved changes will be lost.'"
		/>
	</div>
</template>

<script>
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import Button from '@/components/forms/button/button';
import ButtonGroup from '@/components/forms/button/button-group';
import Spinner from '@/components/visual/spinner/spinner';
import Modals from '@/components/shells/modal/modals';
import CCLSelector from '@/components/ccl-selector/ccl-selector.vue';
import Heading from '@/components/visual/heading.vue';
import ToastV2 from '@/components/notifications/toast/toast-v2';
import { mapActions, mapGetters } from 'vuex';

//constants
import {
	MODULE_NAME,
	SET_BETA_FEATURE,
	BETA_FEATURE,
	SAVE_BETA_FEATURE,
	GO_LIVE_BETA_FEATURE
} from '../beta-features-mutation-types';

import _ from 'lodash';

export default {
	name: 'BetaFeaturesSettings',
	components: {
		Breadcrumbs,
		Button,
		ButtonGroup,
		Spinner,
		Modals,
		CCLSelector,
		Heading,
		ToastV2
	},
	props: {
		id: {
			type: Number,
			require: true
		}
	},
	data() {
		return {
			selected: [],
			originalItems: [],
			items: [],
			loading: false,
			showGoLiveModal: false,
			titleGoLiveModal: '',
			displayFooter: false,
			defaultUrlNames: [
				{ url: '/features', name: 'Test Beta Feature Manager' },
				{ name: 'Settings' }
			],
			enabledSaveBttn: false,
			displayConfirmationModal: false,
			toastMessage: '',
			successfullySaved: false
		};
	},
	computed: {
		...mapGetters(MODULE_NAME, [BETA_FEATURE]),

		betaFeatureDescription() {
			return _.get(this, `${BETA_FEATURE}.featureDescription`, '');
		},

		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);

			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}

			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.defaultUrlNames;
		},
		showGoLiveButton() {
			return this.validatePermission('featuresGoLive.read');
		}
	},
	async created() {
		this.$store.commit('features/clearAccordionStatuses');

		this.loading = true;
		this.$store.commit('features/setBetaFeature', {});
		const hasValue = await this.setBetaFeature(this.id);

		if (!hasValue) {
			this.$router.push({ path: '/features' });
		}

		if (hasValue && !_.isEmpty(this[BETA_FEATURE].items)) {
			this.originalItems = _.cloneDeep(this[BETA_FEATURE].items);
			this.items = _.cloneDeep(this[BETA_FEATURE].items);
		}
		this.loading = false;
	},
	methods: {
		...mapActions(MODULE_NAME, {
			[SET_BETA_FEATURE]: 'setBetaFeature',
			[SAVE_BETA_FEATURE]: 'saveBetaFeature',
			[GO_LIVE_BETA_FEATURE]: 'goLiveBetaFeature'
		}),
		async goLive() {
			const self = this;
			if (!self.titleGoLiveModal) {
				self.titleGoLiveModal = `Are you sure you want to enable ${self.betaFeatureDescription} feature for all clients?`;
			}
			this.showGoLiveModal = true;
		},
		async confirmGoLive() {
			await this.goLiveBetaFeature(this.id);
			this.cancel();
		},
		cancelGoLive() {
			this.showGoLiveModal = false;
		},

		async save() {
			const getCustomersWithActiveCompanies = customers => {
				return customers.map(customer => {
					if (customer.activeBetaFeature) {
						return {
							customerId: customer.id,
							activeCompanies: [0]
						};
					}

					const activeCompanies = customer.companies.reduce(
						(acc, company) => {
							if (company.activeBetaFeature) {
								acc.push(company.companyId);
							}
							const activeLocations = company.locations
								.filter(location => location.activeBetaFeature)
								.map(location => location.locationId);

							if (!_.isEmpty(activeLocations)) {
								acc.push(...activeLocations);
							}

							return acc;
						},
						[]
					);

					return {
						customerId: customer.id,
						activeCompanies: activeCompanies.length
							? activeCompanies
							: []
					};
				});
			};

			const customers = getCustomersWithActiveCompanies(this.selected);

			await this.saveBetaFeature({ id: this.id, customers }).then(res => {
				const { isSaved, message } = res;
				if (isSaved) {
					this.successfullySaved = true;
					this.cancel();
				}
				if (!isSaved) {
					this.toastMessage = message;
					this.$nextTick(() => {
						this.$nextTick(() => {
							if (this.$refs.simpleToastRef) {
								this.$refs.simpleToastRef.show();
							}
						});
					});
				}
			});
		},
		cancel() {
			this.$router.push({
				path: `/features`
			});
		},

		updateItems(data) {
			this.items = data;
			this.enabledSaveBttn = this.checkItemsChanges();
		},
		updateClientsSelected(value) {
			this.selected = value;
		},

		checkItemsChanges() {
			const differences = _.differenceWith(
				this.items,
				this.originalItems,
				_.isEqual
			);
			return differences.length > 0;
		}
	},
	async beforeRouteLeave(to, from, next) {
		if (this.successfullySaved) {
			next();
			return;
		}
		if (this.enabledSaveBttn) {
			this.displayConfirmationModal = true;

			const response = await this.$refs.confirmationDialogue.confirm();
			if (response) {
				next();
			} else {
				next(false);
			}
		} else {
			next();
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Beta Feature Settings Page
	* Can use SCSS variables = TRUE
 */

::v-deep .selection-list__available {
	@include mq($until: md-l) {
		.selection-list__list-header {
			flex-direction: column;
			align-items: unset;

			.search {
				margin: $gutter-md 0;
			}
		}
	}
}

.content-page {
	/* &__heading {
		@include mq($until: sm-l) {
			flex-direction: column;
			align-items: unset;
		}
	} */
	&__actions {
		@include mq($until: md) {
			margin-top: $gutter-md;
		}
	}
}

.beta-features-settings-content {
	background: $white;
	padding: $padding-md;
	height: 64vh;
	display: flex;
	flex-direction: column;
	gap: 12px;
	border: 1px solid $neutral-3;
	border-radius: 4px;

	@include mq($until: lg) {
		height: 73vh;
	}

	&__header-horizontal-line {
		@include create-horizontal-line(16px, 1px, $gutter-xl, $neutral-3);
	}

	&__footer {
		margin-top: $gutter-lg;
		padding: $padding-md 0;
		text-align: right;

		&-container {
			margin-left: $gutter-md; //matches header
			margin-right: $gutter-md; //matches header

			@include mq($from: sm-l) {
				display: flex;
				flex-wrap: wrap;
				justify-content: flex-end;
				align-items: center;
			}

			@include mq($from: lg) {
				@include contain(1200px);
			}
		}

		&-actions {
			display: inline-flex;
			align-items: initial;
		}
	}
}

.is-fixed {
	position: fixed;
	background: $white;
	bottom: 0;
	left: 0;
	width: 100%;
	text-align: right;
	@include create-top-vertical-shadow($neutral-2);
	z-index: 99999;
}
</style>
